"use client"
import { useState, useRef, useEffect } from "react"

const IS_DEV = process.env.NEXT_PUBLIC_TB_MODE === "test"
const API_BASE = IS_DEV ? "https://dev.maxiaohash.top" : ""
type LogEntry = { time: string; msg: string; ok: boolean }
type Pos = { x: number; y: number }
type Size = { w: number; h: number }

function FloatPanel({ title, pos, setPos, size, setSize, open, setOpen, extra, children }: {
  title: string; pos: Pos; setPos: (p: Pos) => void
  size: Size; setSize: (s: Size) => void
  open: boolean; setOpen: (o: boolean) => void
  extra?: React.ReactNode; children: React.ReactNode
}) {
  const dragRef = useRef<{ sx: number; sy: number; ix: number; iy: number } | null>(null)
  const resizeRef = useRef<{ sx: number; sy: number; iw: number; ih: number } | null>(null)

  const onDrag = (e: React.MouseEvent) => {
    dragRef.current = { sx: e.clientX, sy: e.clientY, ix: pos.x, iy: pos.y }
    const move = (ev: MouseEvent) => {
      if (!dragRef.current) return
      setPos({ x: dragRef.current.ix + ev.clientX - dragRef.current.sx, y: dragRef.current.iy + ev.clientY - dragRef.current.sy })
    }
    const up = () => { dragRef.current = null; window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up) }
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up)
  }

  const onResize = (e: React.MouseEvent) => {
    e.stopPropagation()
    resizeRef.current = { sx: e.clientX, sy: e.clientY, iw: size.w, ih: size.h }
    const move = (ev: MouseEvent) => {
      if (!resizeRef.current) return
      setSize({ w: Math.max(200, resizeRef.current.iw + ev.clientX - resizeRef.current.sx), h: Math.max(80, resizeRef.current.ih + ev.clientY - resizeRef.current.sy) })
    }
    const up = () => { resizeRef.current = null; window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up) }
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up)
  }

  return (
    <div className="fixed z-40 shadow-lg rounded-lg border bg-background overflow-hidden"
      style={{ left: pos.x, top: pos.y, width: size.w }}>
      <div className="flex items-center justify-between px-3 py-1.5 border-b cursor-move select-none bg-muted"
        onMouseDown={onDrag}>
        <span className="text-xs font-semibold">{title}</span>
        <div className="flex gap-2 items-center">
          {extra}
          <button onClick={() => setOpen(!open)} className="text-xs text-muted-foreground hover:text-foreground">
            {open ? "收起" : "展开"}
          </button>
        </div>
      </div>
      {open && (
        <div style={{ height: size.h }} className="overflow-auto">
          {children}
        </div>
      )}
      {open && (
        <div className="fixed w-3 h-3 cursor-se-resize z-50" onMouseDown={onResize}
          style={{ left: pos.x + size.w - 12, top: pos.y + size.h + 28, background: "linear-gradient(135deg, transparent 50%, #888 50%)" }} />
      )}
    </div>
  )
}

const getLS = (key: string, def: object) => {
  try { return JSON.parse(localStorage.getItem(key) || "") } catch { return def }
}
const setLS = (key: string, val: object) => localStorage.setItem(key, JSON.stringify(val))

export default function TestPage() {
  const [authed, setAuthed] = useState(false)
  const [pwd, setPwd] = useState("")
  const [pwdErr, setPwdErr] = useState("")
  const [log, setLog] = useState<LogEntry[]>([])
  const [accessToken, setAccessToken] = useState("")
  const [refreshToken, setRefreshToken] = useState("")
  const [loginUser, setLoginUser] = useState("test_admin")
  const [loginPwd, setLoginPwd] = useState("secret")
  const [refreshInput, setRefreshInput] = useState("")

  const [artTitle, setArtTitle] = useState("测试文章")
  const [artSlug, setArtSlug] = useState("test-article-ts")
  const [artContent, setArtContent] = useState("# 测试\n\n这是内容")
  const [artId, setArtId] = useState("")
  const [artListId, setArtListId] = useState("")

  const [tokenOpen, setTokenOpen] = useState(true)
  const [logOpen, setLogOpen] = useState(true)
  const [tokenPos, setTokenPosRaw] = useState<Pos>(() => getLS("tc_tokenPos", { x: 8, y: 80 }) as Pos)
  const [tokenSize, setTokenSizeRaw] = useState<Size>(() => getLS("tc_tokenSize", { w: 260, h: 60 }) as Size)
  const [logPos, setLogPosRaw] = useState<Pos>(() => getLS("tc_logPos", { x: 8, y: 220 }) as Pos)
  const [logSize, setLogSizeRaw] = useState<Size>(() => getLS("tc_logSize", { w: 260, h: 200 }) as Size)

  const setTokenPos = (p: Pos) => { setTokenPosRaw(p); setLS("tc_tokenPos", p) }
  const setTokenSize = (s: Size) => { setTokenSizeRaw(s); setLS("tc_tokenSize", s) }
  const setLogPos = (p: Pos) => { setLogPosRaw(p); setLS("tc_logPos", p) }
  const setLogSize = (s: Size) => { setLogSizeRaw(s); setLS("tc_logSize", s) }

  const print = (msg: string, ok = true) =>
    setLog(prev => [...prev, { time: new Date().toLocaleTimeString(), msg, ok }])

  const handleAuth = async () => {
    const res = await fetch("/api/test-auth", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password: pwd }) })
    if (res.ok) setAuthed(true); else setPwdErr("密码错误")
  }

  const req = async (method: string, path: string, body?: object, token?: string) => {
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        method,
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: body ? JSON.stringify(body) : undefined,
      })
      const data = await res.json()
      print(`${method} ${path} [${res.status}] ${JSON.stringify(data)}`, res.ok)
      return { ok: res.ok, data }
    } catch (e) { print(`${method} ${path} 失败: ${e}`, false) }
  }

  if (!authed) return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-3">
      <h1 className="text-xl font-semibold">测试控制台</h1>
      <input type="password" placeholder="访问密码" value={pwd} className="border px-3 py-2 rounded w-64 text-sm"
        onChange={e => setPwd(e.target.value)} onKeyDown={e => e.key === "Enter" && handleAuth()} />
      <button onClick={handleAuth} className="px-4 py-2 bg-black text-white rounded text-sm">进入</button>
      {pwdErr && <p className="text-red-500 text-sm">{pwdErr}</p>}
    </div>
  )

  return (
    <>
      <FloatPanel title="Token 状态" pos={tokenPos} setPos={setTokenPos} size={tokenSize} setSize={setTokenSize} open={tokenOpen} setOpen={setTokenOpen}>
        <div className="p-2 text-xs font-mono space-y-1">
          <p><b>access:</b></p>
          <div className="overflow-x-auto whitespace-nowrap bg-muted rounded px-2 py-1 cursor-text select-all">{accessToken || "未登录"}</div>
          <p><b>refresh:</b></p>
          <div className="overflow-x-auto whitespace-nowrap bg-muted rounded px-2 py-1 cursor-text select-all">{refreshToken || "-"}</div>
        </div>
      </FloatPanel>

      <FloatPanel title="日志" pos={logPos} setPos={setLogPos} size={logSize} setSize={setLogSize} open={logOpen} setOpen={setLogOpen}
        extra={<button onClick={() => setLog([])} className="text-xs text-muted-foreground hover:text-foreground">清空</button>}>
        <LogContent log={log} />
      </FloatPanel>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <h1 className="text-2xl font-bold">测试控制台</h1>

        <ModuleGrid>
          <ModuleCard title="System" icon="⚙️">
            <Row label="健康检查" onRun={() => req("GET", "/health")} />
          </ModuleCard>

          <ModuleCard title="Auth" icon="🔐">
            <div className="flex gap-2 items-center">
              <span className="text-xs text-muted-foreground">快速填充：</span>
              {[
                { label: "Admin", user: "test_admin" },
                { label: "Editor", user: "test_editor" },
                { label: "Viewer", user: "test_viewer" },
              ].map(({ label, user }) => (
                <button key={label} onClick={() => { setLoginUser(user); setLoginPwd("secret") }}
                  className="px-2 py-1 text-xs border rounded hover:bg-muted transition-colors">
                  {label}
                </button>
              ))}
            </div>
            <Row label="登录" onRun={async () => {
              const r = await req("POST", "/api/blog_admin/auth/login?tb_mode=test", { username: loginUser, password: loginPwd })
              if (r?.data?.access_token) { setAccessToken(r.data.access_token); setRefreshToken(r.data.refresh_token); setRefreshInput(r.data.refresh_token) }
            }}>
              <Input label="用户名" value={loginUser} onChange={setLoginUser} />
              <Input label="密码" value={loginPwd} onChange={setLoginPwd} type="password" />
            </Row>
            <Row label="刷新 Token" onRun={async () => {
              const r = await req("POST", "/api/blog_admin/auth/refresh?tb_mode=test", { refresh_token: refreshInput })
              if (r?.data?.access_token) setAccessToken(r.data.access_token)
            }}>
              <Input label="refresh_token" value={refreshInput} onChange={setRefreshInput} />
            </Row>
            <Row label="登出" onRun={() => req("POST", "/api/blog_admin/auth/logout?tb_mode=test", undefined, accessToken)}>
              <button onClick={() => { setAccessToken(""); setRefreshToken(""); setRefreshInput("") }}
                className="px-3 py-1.5 border rounded text-sm hover:bg-muted transition-colors text-muted-foreground">
                清空 Token
              </button>
            </Row>
          </ModuleCard>

          <ModuleCard title="User" icon="👤" disabled />
          <ModuleCard title="Article" icon="📝">
            <Row label="文章列表" onRun={() => req("GET", `/api/blog/articles?tb_mode=test&status=published`)} />
            <Row label="获取文章" onRun={() => req("GET", `/api/blog/articles/${artListId}?tb_mode=test`)}>
              <Input label="article_id" value={artListId} onChange={setArtListId} />
            </Row>
            <Row label="新建文章" onRun={async () => {
              const r = await req("POST", "/api/blog_admin/articles?tb_mode=test", {
                title: artTitle, slug: artSlug + "-" + Date.now(),
                content: artContent, status: "published",
              }, accessToken)
              if (r?.data?.article_id) setArtId(r.data.article_id)
            }}>
              <Input label="标题" value={artTitle} onChange={setArtTitle} />
              <Input label="slug" value={artSlug} onChange={setArtSlug} />
            </Row>
            <Row label="更新文章" onRun={() => req("PUT", `/api/blog_admin/articles/${artId}?tb_mode=test`, {
              title: artTitle + "(已更新)", change_note: "测试更新",
            }, accessToken)}>
              <Input label="article_id" value={artId} onChange={setArtId} />
            </Row>
            <Row label="版本列表" onRun={() => req("GET", `/api/blog_admin/articles/${artId}/versions?tb_mode=test`, undefined, accessToken)} />
            <Row label="软删除" onRun={() => req("DELETE", `/api/blog_admin/articles/${artId}?tb_mode=test`, undefined, accessToken)} />
          </ModuleCard>
          <ModuleCard title="Tag" icon="🏷️" disabled />
          <ModuleCard title="Category" icon="📁" disabled />
          <ModuleCard title="Upload" icon="📤" disabled />
          <ModuleCard title="Version" icon="🔄" disabled />
          <ModuleCard title="Stats" icon="📊" disabled />
          <ModuleCard title="Search" icon="🔍" disabled />
          <ModuleCard title="Comment" icon="💬" disabled />
          <ModuleCard title="Media" icon="🖼️" disabled />
          <ModuleCard title="Config" icon="🛠️" disabled />
          <ModuleCard title="Log" icon="📋" disabled />
          <ModuleCard title="Cache" icon="⚡" disabled />
          <ModuleCard title="Backup" icon="💾" disabled />
        </ModuleGrid>
      </div>
    </>
  )
}

function ModuleGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-4 gap-3">{children}</div>
}

function ModuleCard({ title, icon, children, disabled }: { title: string; icon: string; children?: React.ReactNode; disabled?: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button onClick={() => !disabled && setOpen(o => !o)}
        className={`flex flex-col items-center justify-center gap-1 border rounded p-4 text-center transition-colors
          ${disabled ? "opacity-30 cursor-not-allowed" : "hover:bg-muted cursor-pointer"}
          ${open ? "bg-muted" : ""}`}>
        <span className="text-2xl">{icon}</span>
        <span className="text-xs font-medium">{title}</span>
      </button>
      {open && !disabled && (
        <div className="col-span-4 border rounded p-4 space-y-3">
          <div className="flex justify-between items-center mb-2">
            <span className="font-medium text-sm">{icon} {title}</span>
            <button onClick={() => setOpen(false)} className="text-xs text-muted-foreground hover:text-foreground">收起</button>
          </div>
          {children}
        </div>
      )}
    </>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border rounded p-4 space-y-3">
      <h2 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{title}</h2>
      {children}
    </div>
  )
}

function Row({ label, onRun, children }: { label: string; onRun: () => void; children?: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
      <div className="flex-1 space-y-2">{children}</div>
      <button onClick={onRun} className="px-3 py-1.5 border rounded text-sm hover:bg-muted transition-colors whitespace-nowrap">{label}</button>
    </div>
  )
}

function LogContent({ log }: { log: LogEntry[] }) {
  const bottomRef = useRef<HTMLDivElement>(null)
  useEffect(() => { bottomRef.current?.scrollIntoView() }, [log])
  return (
    <div className="min-h-full font-mono text-xs bg-black p-2 space-y-0.5">
      {log.length === 0 ? <p className="text-zinc-500">等待操作...</p>
        : log.map((l, i) => <p key={i} className={l.ok ? "text-green-400" : "text-red-400"}>[{l.time}] {l.msg}</p>)}
      <div ref={bottomRef} />
    </div>
  )
}

function Input({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  const [show, setShow] = useState(true)
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground w-20 shrink-0">{label}</span>
      <div className="relative flex-1 min-w-0">
        <input type={type === "password" ? (show ? "text" : "password") : type} value={value}
          onChange={e => onChange(e.target.value)} className="border rounded px-2 py-1 text-sm w-full pr-7" />
        {type === "password" && (
          <button type="button" onClick={() => setShow(s => !s)}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs">
            {show ? "隐" : "显"}
          </button>
        )}
      </div>
    </div>
  )
}
